import { deleteField } from "firebase/firestore";

/**
 * Build the merge patch that locks a slot's vote to a manual value and
 * clears the engine fields for that slot.
 *
 * Chunk E1: voteAutoReason is the slot's permanent reality (the "what"
 * caused the alignment / conflict) and is left untouched on override. Only
 * the manual-lock invariant fields are cleared:
 *   - voteAutoSource[slotId] = null  → tells the engine "do not auto-touch this slot"
 *   - voteProposed[slotId]   = false → drops the "(Auto)" chip so the UI
 *                                       reflects the user's manual choice
 *
 * The persistent ⚠/⭐/✓/ⓘ indicator continues to render based on
 * voteAutoReason (which still describes the engine's view of the world).
 *
 * Chunk H2 (acknowledge-on-vote): when the slot has currently-active
 * conflict refIds, the override patch ALSO appends them to
 * acknowledgedRefIds and clears previousManualVote — so the next engine
 * reeval doesn't immediately re-fire override on the same conflict. This
 * lets a manual click-to-vote on a known-conflict slot act as an implicit
 * acknowledgement (or for an auto-conflict-override slot, as Restore /
 * Confirm-with-arbitrary-value).
 *
 * @param {string} slotId
 * @param {"PREFERRED"|"FEASIBLE"|null} nextManual  null → user wants NO
 *   (delete the votes-map entry). Non-null → set votes-map entry.
 * @param {Object} [opts]
 * @param {string[]} [opts.currentRefIds]  All conflict refIds present at
 *   the moment the user clicks. When non-empty, the patch appends them to
 *   acknowledgedRefIds and clears previousManualVote. When empty (the
 *   pre-H2 default), voteAutoReason is left untouched.
 * @param {string[]} [opts.existingAcknowledgedRefIds]  Existing
 *   acknowledgedRefIds from the slot's reason. Merged with currentRefIds;
 *   duplicates removed.
 * @returns {object} merge-patch payload for setDoc(..., { merge: true })
 */
export function buildOverridePatch(
  slotId,
  nextManual,
  { currentRefIds = [], existingAcknowledgedRefIds = [] } = {},
) {
  if (!slotId) throw new Error("slotId is required");
  const base = {
    votes: {
      [slotId]: nextManual == null ? deleteField() : nextManual,
    },
    voteAutoSource: { [slotId]: null },
    voteProposed: { [slotId]: false },
  };
  if (!Array.isArray(currentRefIds) || currentRefIds.length === 0) {
    return base;
  }
  const merged = Array.from(
    new Set([...(existingAcknowledgedRefIds || []), ...currentRefIds]),
  );
  return {
    ...base,
    voteAutoReason: {
      [slotId]: {
        acknowledgedRefIds: merged,
        previousManualVote: deleteField(),
      },
    },
  };
}

/**
 * Build the merge patch that restores a manual vote that the engine
 * overrode (Chunk G6). Re-locks the slot manually + appends ALL current
 * conflict refIds to acknowledgedRefIds + clears previousManualVote.
 *
 * The Restore action both restores the user's prior vote AND acknowledges
 * the conflicts that the engine flagged — so the urgent (red) treatment
 * downgrades to plain (orange) once the write lands. A NEW refId in a
 * future calendar-sync re-fires the urgent treatment.
 *
 * @param {string} slotId
 * @param {"PREFERRED"|"FEASIBLE"|null} previousManualVote  The vote
 *   captured at override time. If null, the user ends up with no vote
 *   (a manually-cleared slot — equivalent to Confirm but without the
 *   override marker).
 * @param {string[]} currentRefIds  All conflict refIds present at the
 *   moment the user clicks Restore.
 * @param {string[]} existingAcknowledged  Existing acknowledgedRefIds
 *   from the slot's reason. Merged with currentRefIds; duplicates removed.
 * @returns {object} merge-patch payload for setDoc(..., { merge: true })
 */
export function buildRestorePatch(
  slotId,
  previousManualVote,
  currentRefIds,
  existingAcknowledged = [],
) {
  if (!slotId) throw new Error("slotId is required");
  const merged = Array.from(
    new Set([...(existingAcknowledged || []), ...(currentRefIds || [])]),
  );
  return {
    votes: {
      [slotId]: previousManualVote == null ? deleteField() : previousManualVote,
    },
    voteAutoSource: { [slotId]: null },
    voteAutoReason: {
      [slotId]: {
        acknowledgedRefIds: merged,
        previousManualVote: deleteField(),
      },
    },
    voteProposed: { [slotId]: false },
  };
}

/**
 * Build the merge patch when the user accepts the engine's override
 * (Chunk G6). Vote stays cleared, voteAutoSource stays
 * "auto-conflict-override", acknowledgedRefIds extended with all current
 * refIds, previousManualVote cleared (no longer needed for Restore prompts).
 *
 * @param {string} slotId
 * @param {string[]} currentRefIds  All conflict refIds present at the
 *   moment the user clicks Confirm.
 * @param {string[]} existingAcknowledged  Existing acknowledgedRefIds
 *   from the slot's reason. Merged with currentRefIds; duplicates removed.
 * @returns {object} merge-patch payload for setDoc(..., { merge: true })
 */
export function buildConfirmOverridePatch(
  slotId,
  currentRefIds,
  existingAcknowledged = [],
) {
  if (!slotId) throw new Error("slotId is required");
  const merged = Array.from(
    new Set([...(existingAcknowledged || []), ...(currentRefIds || [])]),
  );
  return {
    voteAutoReason: {
      [slotId]: {
        acknowledgedRefIds: merged,
        previousManualVote: deleteField(),
      },
    },
  };
}
